import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Th, Td, Empty, Bar } from "../components/shared";
import { inr, fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import ExportMenu from "../components/ExportMenu";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Download, Boxes, AlertTriangle, ClipboardCheck, Pencil } from "lucide-react";

const emptyForm = { item_name: "", category: "Consumables", branch: "Sarita Vihar", godown: "", unit: "Piece", opening_stock: 0, received: 0, used: 0, reorder_level: 0, unit_cost: 0, last_audit_date: "", physical_count: "", audited_by: "", remarks: "" };

export default function Inventory() {
  const { meta, branch } = useApp();
  const [rows, setRows] = useState([]);
  const [lowOnly, setLowOnly] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [audit, setAudit] = useState(null);

  const load = useCallback(() => {
    api.get("/inventory", { params: { branch, low_only: lowOnly } }).then((r) => setRows(r.data));
  }, [branch, lowOnly]);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => { setEditId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (r) => {
    setEditId(r.id);
    setForm({ item_name: r.item_name, category: r.category, branch: r.branch, godown: r.godown || "", unit: r.unit || "Piece",
      opening_stock: r.opening_stock, received: r.received, used: r.used, reorder_level: r.reorder_level, unit_cost: r.unit_cost,
      last_audit_date: r.last_audit_date || "", physical_count: r.physical_count ?? "", audited_by: r.audited_by || "", remarks: r.remarks || "" });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.item_name) { toast.error("Item name required"); return; }
    const payload = { ...form };
    ["opening_stock", "received", "used", "reorder_level", "unit_cost"].forEach((k) => payload[k] = Number(payload[k]));
    payload.physical_count = payload.physical_count === "" ? null : Number(payload.physical_count);
    if (editId) { await api.put(`/inventory/${editId}`, payload); toast.success("Inventory item updated"); }
    else { await api.post("/inventory", payload); toast.success("Inventory item added"); }
    setOpen(false); setEditId(null); setForm(emptyForm); load();
  };

  const submitAudit = async () => {
    const pc = Number(audit.physical_count);
    await api.put(`/inventory/${audit.id}`, { physical_count: pc, last_audit_date: audit.last_audit_date, audited_by: audit.audited_by, remarks: audit.remarks });
    toast.success("Stock audited & delivery logged"); setAudit(null); load();
  };

  const lowCount = rows.filter((r) => r.low_stock).length;
  const COLS = ["Item", "Branch / Godown", "Opening", "Received", "Used", "Current", "Reorder", "Unit Cost", "Valuation", "Last Audit", "Variance", ""];

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">Multi-Branch Inventory & Audit</h1>
          <p className="text-brand-slate text-sm mt-1">Stock levels, valuation & physical audit reconciliation</p>
        </div>
        <div className="flex items-center gap-3">
          {lowCount > 0 && <span className="flex items-center gap-1.5 text-sm font-semibold text-rose-600 bg-rose-50 px-3 py-1.5 rounded-lg"><AlertTriangle className="w-4 h-4" /> {lowCount} low stock</span>}
          <div className="flex items-center gap-2"><Switch data-testid="low-stock-toggle" checked={lowOnly} onCheckedChange={setLowOnly} /><Label className="text-sm text-brand-slate">Low stock only</Label></div>
          <ExportMenu dataset="inventory" testid="export-inventory" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-inventory-button" onClick={openCreate} className="gap-2 bg-brand-teal hover:bg-brand-tealdark active:scale-95 transition-transform"><Plus className="w-4 h-4" /> Add Item</Button></DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Inventory Item" : "Add Inventory Item"}</DialogTitle>
                <DialogDescription>Current stock and valuation are computed from opening + received − used.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Item Name</Label><Input data-testid="inv-item" value={form.item_name} onChange={(e) => setF("item_name", e.target.value)} /></div>
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setF("category", v)}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Branch</Label>
                  <Select value={form.branch} onValueChange={(v) => setF("branch", v)}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.branches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Godown / Store</Label><Input value={form.godown} onChange={(e) => setF("godown", e.target.value)} /></div>
                <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setF("unit", e.target.value)} /></div>
                <div><Label>Opening Stock</Label><Input type="number" value={form.opening_stock} onChange={(e) => setF("opening_stock", e.target.value)} /></div>
                <div><Label>Received</Label><Input type="number" value={form.received} onChange={(e) => setF("received", e.target.value)} /></div>
                <div><Label>Used / Issued</Label><Input type="number" value={form.used} onChange={(e) => setF("used", e.target.value)} /></div>
                <div><Label>Reorder Level</Label><Input type="number" value={form.reorder_level} onChange={(e) => setF("reorder_level", e.target.value)} /></div>
                <div><Label>Unit Cost (₹)</Label><Input type="number" value={form.unit_cost} onChange={(e) => setF("unit_cost", e.target.value)} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="inv-submit" onClick={submit} className="bg-brand-teal hover:bg-brand-tealdark">{editId ? "Save Changes" : "Add Item"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-border"><tr>{COLS.map((c) => <Th key={c}>{c}</Th>)}</tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={COLS.length}><Empty icon={Boxes} msg="No inventory items" /></td></tr> :
                rows.map((r) => (
                  <tr key={r.id} data-testid="inventory-row" className={`border-b border-border/60 hover:bg-slate-50 transition-colors ${r.low_stock ? "bg-rose-50/50" : ""}`}>
                    <Td className="font-medium max-w-[200px] truncate">{r.item_name}{r.low_stock && <span data-testid="low-stock-badge" className="ml-2 text-[10px] font-bold text-rose-600 bg-rose-100 px-1.5 py-0.5 rounded">LOW</span>}</Td>
                    <Td className="text-slate-600 text-xs">{r.branch}<br /><span className="text-slate-400">{r.godown}</span></Td>
                    <Td>{r.opening_stock}</Td>
                    <Td className="text-brand-emerald">+{r.received}</Td>
                    <Td className="text-slate-600">-{r.used}</Td>
                    <Td className={`font-bold ${r.low_stock ? "text-rose-600" : "text-navy"}`}>{r.current_stock} {r.unit}</Td>
                    <Td className="text-slate-600">{r.reorder_level}</Td>
                    <Td className="tabular-nums">{inr(r.unit_cost)}</Td>
                    <Td className="tabular-nums font-semibold">{inr(r.total_valuation)}</Td>
                    <Td className="text-slate-600 text-xs">{fmtDate(r.last_audit_date)}</Td>
                    <Td className={r.audit_variance ? "text-amber-600 font-semibold" : "text-slate-500"}>{r.audit_variance == null ? "—" : (r.audit_variance > 0 ? `+${r.audit_variance}` : r.audit_variance)}</Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <button data-testid="edit-inventory" onClick={() => openEdit(r)} className="text-slate-400 hover:text-brand-teal"><Pencil className="w-4 h-4" /></button>
                        <button data-testid="audit-button" onClick={() => setAudit({ ...r, physical_count: r.physical_count ?? r.current_stock, last_audit_date: new Date().toISOString().slice(0, 10) })} className="text-brand-teal hover:text-brand-tealdark"><ClipboardCheck className="w-4 h-4" /></button>
                      </div>
                    </Td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!audit} onOpenChange={(o) => !o && setAudit(null)}>
        <DialogContent className="max-w-md" data-testid="audit-modal">
          {audit && <>
            <DialogHeader><DialogTitle className="font-heading">Log Delivery & Audit Stock</DialogTitle>
              <DialogDescription>Enter the physical count; the discrepancy versus system stock is calculated live.</DialogDescription>
            </DialogHeader>
            <p className="text-sm text-brand-slate">{audit.item_name} · {audit.branch}</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 rounded-lg p-3"><div className="text-[10px] uppercase tracking-widest text-brand-slate">System Stock</div><div className="font-heading font-bold text-navy text-xl">{audit.current_stock}</div></div>
              <div><Label>Physical Count</Label><Input data-testid="audit-count" type="number" value={audit.physical_count} onChange={(e) => setAudit((a) => ({ ...a, physical_count: e.target.value }))} /></div>
              <div className="col-span-2">
                <div className={`rounded-lg p-3 text-sm font-semibold ${Number(audit.physical_count) - audit.current_stock === 0 ? "bg-emerald-50 text-brand-emerald" : "bg-amber-50 text-amber-700"}`}>
                  Discrepancy: {Number(audit.physical_count) - audit.current_stock > 0 ? "+" : ""}{Number(audit.physical_count) - audit.current_stock} {audit.unit}
                </div>
              </div>
              <div><Label>Audit Date</Label><Input type="date" value={audit.last_audit_date} onChange={(e) => setAudit((a) => ({ ...a, last_audit_date: e.target.value }))} /></div>
              <div><Label>Audited By</Label><Input value={audit.audited_by || ""} onChange={(e) => setAudit((a) => ({ ...a, audited_by: e.target.value }))} /></div>
              <div className="col-span-2"><Label>Remarks</Label><Textarea value={audit.remarks || ""} onChange={(e) => setAudit((a) => ({ ...a, remarks: e.target.value }))} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAudit(null)}>Cancel</Button>
              <Button data-testid="audit-submit" onClick={submitAudit} className="bg-brand-teal hover:bg-brand-tealdark">Save Audit</Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
